import toast from "react-hot-toast";
import { useEffect, useMemo, useRef, useState } from "react";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { getTrailOffers } from "@/services/offers";
import { useScreenPermission } from "@/hooks/useScreenPermission";

type OfferItem = {
  title?: string;
  Name?: string;
  Discount_Amount?: number;
  description?: string;
};

interface PaymentModalProps {
  open: boolean;
  onClose: () => void;
  data: any;
  onBuyNow: () => void;

  rewardBalance: number;

  offers?: OfferItem[];
  specialOffers?: OfferItem[];

  selectedOfferAmount: number;
  setSelectedOfferAmount: (
    v: number
  ) => void;
  selectedSpecialOfferAmount: number;
  setSelectedSpecialOfferAmount: (
    v: number
  ) => void;
  usedRewardAmount: number;
  setUsedRewardAmount: (
    v: number
  ) => void;

  paymentCalculation: any;
  showSavingsModal: boolean;
  onStartTrial: (childIds: number[]) => Promise<void>;
}

export default function PaymentModal({
  open,
  onClose,
  data,
  onBuyNow,
  rewardBalance,
  offers = [],
  specialOffers = [],
  selectedOfferAmount,
  setSelectedOfferAmount,
  selectedSpecialOfferAmount,
  setSelectedSpecialOfferAmount,
  usedRewardAmount,
  setUsedRewardAmount,
  paymentCalculation,
  showSavingsModal,
  onStartTrial
}: PaymentModalProps) {
  if (!open || !data)
    return null;
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const userDetails = useCurrentUser();
  const { trail_status: trialStatus ,role} = userDetails;
  const [trialOffer, setTrialOffer] = useState<any>(null);
    const {canView} = useScreenPermission("trails offer");
  const availableOffers = useMemo(
    () =>
      Array.isArray(offers)
        ? offers.filter(
          (offer) =>
            Number(offer?.Discount_Amount || 0) > 0
        )
        : [],
    [offers]
  );

  useEffect(() => {
    if (!open) return;

    const fetchTrialOffer = async () => {
      try {
        // setLoadingTrial(true);

        const res = await getTrailOffers('True',undefined,'');
        if (res.status === 200) {
          const responseData = res.data as any;
          const activeOffer = responseData.find(
        (item: any) => item.Status === true
      );

      setTrialOffer(activeOffer || null);
        }
      } catch (err) {
        console.error(err);
      } finally {
        // setLoadingTrial(false);
      }
    };

if(!trialStatus && canView){
    fetchTrialOffer();}
  }, [open, data?.childIds]);

  useEffect(() => {
    if (!open) {
      setTrialOffer(null);
    }
  }, [open]);

  const imageBaseUrl = import.meta.env.VITE_BASE_URL.replace("/api", "");

  const trialImage =
  trialOffer?.Image?.startsWith("http")
    ? trialOffer?.Image
    : `${imageBaseUrl}${trialOffer?.Image}`;

    const trialDays = Number(trialOffer?.Days || 0);

const dayText = `${trialDays} ${trialDays === 1 ? "Day" : "Days"}`;

return (
    <>
      <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-4 overflow-y-auto">
        <div className="bg-white rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto p-6 shadow-xl">
          <h2 className="text-2xl font-bold text-gray-800 mb-5">
            Subscription Details
          </h2>

          <div className="space-y-3">

            {data.items.map(
              (
                item: any,
                index: number
              ) => (
                <div
                  key={index}
                  className="border rounded-xl p-4 bg-gray-50"
                >
                  <div className="flex justify-between">
                    <span className="font-semibold">
                      {
                        item.planName
                      }
                    </span>

                    <span className="text-green-600 font-bold">
                      ₹
                      {
                        item.amount.toFixed(2)
                      }
                    </span>
                  </div>

                  <div className="text-xs text-gray-500 mt-1">
                    {
                      item.duration
                    }{" "}
                    •{" "}
                    {item.range}KM
                  </div>
                </div>
              )
            )}

            <div className="space-y-5 mt-5">

              {/* Reward Balance */}
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-2xl p-4">

                <div className="flex items-center justify-between">

                  <div>
                    <p className="text-sm text-gray-500">
                      Referral Reward Balance
                    </p>

                    <h3 className="text-2xl font-bold text-orange-600">
                      ₹{rewardBalance || 0}
                    </h3>
                  </div>

                  <div className="text-4xl">
                    🎁
                  </div>

                </div>

                <div className="mt-4 space-y-3">

                  {rewardBalance > 0 && (() => {

                    const maxAllowed =
                      (paymentCalculation?.originalAmount || 0) -
                      selectedOfferAmount -
                      selectedSpecialOfferAmount;

                    const usableAmount = Math.min(
                      rewardBalance,
                      maxAllowed
                    );

                    const isSelected =
                      usedRewardAmount === usableAmount;

                    return (
                      <label
                        className={`flex items-center justify-between border rounded-2xl px-4 py-3 cursor-pointer transition-all ${isSelected
                          ? "border-orange-500 bg-orange-50"
                          : "border-gray-200 hover:border-orange-300"
                          }`}
                      >
                        <div className="flex items-center gap-3">

                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {
                              if (isSelected) {

                                setUsedRewardAmount(0);

                                toast.success(
                                  "Reward removed"
                                );

                              } else {

                                setUsedRewardAmount(
                                  usableAmount
                                );

                                toast.success(
                                  `₹${usableAmount} reward applied 🎉`
                                );
                              }
                            }}
                            className="w-4 h-4 accent-orange-500"
                          />

                          <div>
                            <p className="font-semibold text-gray-800">
                              Use Reward Coins
                            </p>

                            <p className="text-xs text-gray-500">
                              Available Balance: ₹{rewardBalance}
                            </p>
                          </div>

                        </div>

                        <div className="text-orange-600 font-bold">
                          ₹{usableAmount}
                        </div>
                      </label>
                    );
                  })()}

                </div>
              </div>

              {/* Offers */}
              <div>

                <div className="flex items-center justify-between mb-3">

                  <h3 className="font-bold text-lg">
                    Available Offers
                  </h3>

                  {selectedOfferAmount >
                    0 && (
                      <span className="bg-green-100 text-green-700 text-xs px-3 py-1 rounded-full font-semibold">
                        Offer Applied
                      </span>
                    )}

                </div>
                <div className="space-y-3">

                  {availableOffers.length === 0 ? (
                    <div className="border rounded-2xl p-6 text-center bg-gray-50">
                      <div className="text-4xl mb-2">
                        🎁
                      </div>

                      <p className="font-semibold text-gray-700">
                        No Available Offers
                      </p>
                    </div>
                  ) : (availableOffers.map(
                    (
                      offer: any,
                      index: number
                    ) => {
                      const isActive =
                        selectedOfferAmount ===
                        Number(
                          offer.Discount_Amount
                        );

                      return (
                        <div
                          key={index}
                          className={`border rounded-2xl p-4 transition-all cursor-pointer ${isActive
                            ? "border-green-500 bg-green-50 shadow-md"
                            : "hover:border-gray-400"
                            }`}
                          onClick={() => {
                            const amount =
                              Number(
                                offer.Discount_Amount
                              );

                            if (
                              isActive
                            ) {
                              setSelectedOfferAmount(
                                0
                              );

                              toast.success(
                                "Offer removed"
                              );
                            } else {
                              setSelectedOfferAmount(
                                amount
                              );

                              toast.success(
                                `${offer.Discount_Percentage}% offer applied 🎉`
                              );
                            }
                          }}
                        >

                          <div className="flex justify-between items-start">

                            <div>

                              <div className="flex items-center gap-2">

                                <h4 className="font-bold text-lg">
                                  {
                                    offer.title ||
                                    `${offer.Discount_Percentage}% OFF`
                                  }
                                </h4>

                                {isActive && (
                                  <span className="bg-green-600 text-white text-xs px-2 py-1 rounded-full">
                                    ACTIVE
                                  </span>
                                )}

                              </div>

                              <p className="text-sm text-gray-500 mt-1">
                                {offer.description ||
                                  `Save ₹${offer.Discount_Amount} instantly`}
                              </p>

                            </div>

                            <div className="text-right">

                              <div className="text-2xl font-bold text-green-600">
                                ₹
                                {
                                  offer.Discount_Amount
                                }
                              </div>

                              <div className="text-xs text-gray-500">
                                Savings
                              </div>

                            </div>

                          </div>

                          {isActive && (
                            <button
                              type="button"
                              className="mt-4 text-sm text-red-500 font-semibold"
                              onClick={(e) => {
                                e.stopPropagation();

                                setSelectedOfferAmount(
                                  0
                                );

                                toast.success(
                                  "Offer removed"
                                );
                              }}
                            >
                              Remove Offer
                            </button>
                          )}

                        </div>
                      );
                    }
                  ))}

                </div>
              </div>

              <div className="mt-6">

                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-lg text-purple-700">
                    Special Offers
                  </h3>
                </div>

                <div className="space-y-3">

                  {specialOffers?.length === 0 ? (
                    <div className="border rounded-2xl p-6 text-center bg-gray-50">
                      <div className="text-4xl mb-2">
                        🎁
                      </div>

                      <p className="font-semibold text-gray-700">
                        No Special Offers
                      </p>
                    </div>
                  ) : (
                    specialOffers.map(
                      (
                        offer: any,
                        index: number
                      ) => {
                        const amount =
                          Number(
                            offer.Discount_Amount || 0
                          );

                        const isActive =
                          selectedSpecialOfferAmount === amount;

                        return (
                          <div
                            key={index}
                            className={`border rounded-2xl p-4 cursor-pointer ${isActive
                              ? "border-purple-500 bg-purple-50"
                              : ""
                              }`}
                            onClick={() =>
                              setSelectedSpecialOfferAmount(
                                isActive
                                  ? 0
                                  : amount
                              )
                            }
                          >
                            <div className="flex justify-between">

                              <div>

                                <h4 className="font-bold">
                                  🎉
                                  {
                                    offer.title ||
                                    offer.Name ||
                                    "Special Offer"
                                  }
                                </h4>

                                <p className="text-sm text-gray-500">
                                  Save ₹
                                  {
                                    amount
                                  }
                                </p>

                              </div>

                              <div className="text-purple-600 font-bold">
                                ₹{amount}
                              </div>

                            </div>
                            {isActive && (
                              <button
                                type="button"
                                className="mt-3 text-sm text-red-500 font-semibold"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedSpecialOfferAmount(0);
                                  toast.success("Special offer removed");
                                }}
                              >
                                Remove Offer
                              </button>
                            )}
                          </div>
                        );
                      }
                    )
                  )}

                </div>
              </div>

            </div>

            <div className="space-y-2 border-t pt-4 mt-5">

              <div className="flex justify-between text-sm">
  <span>
    Original Amount
  </span>

  <span>
    ₹
    {Number(
      paymentCalculation?.childOriginalAmount ??
      paymentCalculation?.originalAmount ??
      0
    ).toFixed(2)}
  </span>
</div>
{Number(
  paymentCalculation?.childDiscountAmount || 0
) > 0 ? (
  <div className="flex justify-between text-green-600 text-sm">
    <span>
      Child Discount (
      {Number(
        paymentCalculation?.childDiscount || 0
      )}%)
    </span>

    <span>
      - ₹
      {Number(
        paymentCalculation?.childDiscountAmount || 0
      ).toFixed(2)}
    </span>
  </div>
) : (
  <div className="flex justify-between text-sm">
    <span>
      Child Payment
    </span>

    <span className="font-semibold">
      100%
    </span>
  </div>
)}

<div className="flex justify-between text-sm">
  <span>
    Amount After Child Discount
  </span>

  <span>
    ₹
    {Number(
      paymentCalculation?.originalAmount || 0
    ).toFixed(2)}
  </span>
</div>

              {!!paymentCalculation?.offerAmount && (
                <div className="flex justify-between text-green-600 text-sm">
                  <span>
                    Offer Discount
                  </span>

                  <span>
                    - ₹
                    {
                      paymentCalculation?.offerAmount.toFixed(2)
                    }
                  </span>
                </div>
              )}
              {!!paymentCalculation?.specialOfferAmount && (
                <div className="flex justify-between text-purple-600 text-sm">
                  <span>Special Offer</span>
                  <span>
                    - ₹{paymentCalculation.specialOfferAmount.toFixed(2)}
                  </span>
                </div>
              )}

              {!!paymentCalculation?.rewardAmount && (
                <div className="flex justify-between text-green-600 text-sm">
                  <span>
                    Referral Reward
                  </span>

                  <span>
                    - ₹
                    {
                      paymentCalculation?.rewardAmount
                    }
                  </span>
                </div>
              )}

              <div className="flex justify-between border-t pt-3 text-lg font-bold">
                <span>
                  Final Amount
                </span>

                <span className="text-green-600">
                  ₹
                  {
                    Number(
                      paymentCalculation?.finalAmount || 0
                    ).toFixed(2)
                  }
                </span>
              </div>

            </div>
            <div className="mt-5 border rounded-xl p-4 bg-gray-50">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) =>
                    setAcceptedTerms(e.target.checked)
                  }
                  className="mt-1 w-4 h-4 accent-orange-500"
                />

                <span className="text-sm text-gray-700">
                  I have read and agree to the{" "}
                  <button
                    type="button"
                    className="text-orange-600 font-semibold underline"
                    onClick={() =>
                      setShowTermsModal(true)
                    }
                  >
                    Terms & Conditions
                  </button>
                  , Privacy Policy and Disclaimer.
                </span>
              </label>
            </div>
            {!acceptedTerms && (
              <p className="text-xs text-red-500 mt-2">
                Please accept Terms & Conditions to continue payment.
              </p>
            )}
            <div className="mt-6 space-y-3">
              {!trialStatus && trialOffer && role === "Parent" &&(
                <div className="mt-4 overflow-hidden rounded-2xl border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-sm">

                  <div className="p-5">
                    <div className="flex flex-row justify-between align-center">
                      <div>
                        <h3 className="mt-3 text-xl font-bold text-gray-900">
                          Free Trial Available
                        </h3>

                        <p className="mt-2 text-gray-600">
                          Enjoy <span className="font-semibold text-blue-600">{dayText}</span> absolutely free.
                        </p>
                      </div>
                      {trialImage && (
                        <img
                          src={trialImage}
                          alt="Free Trial"
                          className="h-16 w-16 object-cover"
                        />
                      )}
                      </div>
                        <button
                        disabled={!acceptedTerms}
                          onClick={() => onStartTrial(data.childIds)}
                          className={`mt-5 w-full rounded-xl py-3 font-semibold text-white transition ${
  acceptedTerms
    ? "bg-blue-600 hover:bg-blue-700"
    : "bg-gray-300 cursor-not-allowed"
}`}
                        >
                          Start {dayText} Trial
                        </button>

                      </div>
                    </div>
)}

              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 border border-gray-300 rounded-xl py-3"
                >
                  Cancel
                </button>

                <button
                  disabled={!acceptedTerms}
                  onClick={() => {
                    if (!acceptedTerms) {
                      toast.error("Please accept Terms & Conditions");
                      return;
                    }

                    onBuyNow();
                  }}
                  className={`flex-1 text-white rounded-xl py-3 font-semibold ${acceptedTerms
                      ? "bg-green-600 hover:bg-green-700"
                      : "bg-gray-300 cursor-not-allowed"
                    }`}
                >
                  Pay ₹{Number(paymentCalculation?.finalAmount || 0).toFixed(2)}
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>

      {showSavingsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">

          <div className="bg-white rounded-3xl p-8 w-[350px] text-center animate-bounce">

            <div className="text-6xl">
              🎉
            </div>

            <h2 className="text-3xl font-bold mt-3">
              Savings Applied
            </h2>

            <p className="text-gray-500 mt-2">
              You saved
            </p>

            <div className="text-5xl font-bold text-green-600 mt-4">
              ₹
              {
                paymentCalculation.totalSavings
              }
            </div>

            <div className="mt-4 text-sm text-gray-400">
              Redirecting to payment...
            </div>

          </div>
        </div>
      )}
      {showTermsModal && (
        <div className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-5xl h-[90vh] rounded-2xl flex flex-col">

            <div className="flex justify-between items-center border-b p-4">
              <h2 className="text-xl font-bold">
                Terms & Conditions
              </h2>

              <button
                onClick={() =>
                  setShowTermsModal(false)
                }
                className="text-2xl font-bold"
              >
                ×
              </button>
            </div>

            <iframe
              src="/TermsConditions.html"
              title="Terms & Conditions"
              className="flex-1 w-full border-0"
            />
          </div>
        </div>
      )}
    </>
  );
}